declare module '#app' {
  interface NuxtApp {
    $dtrum: Promise<typeof window.dtrum>
  }
}

export default defineNuxtPlugin(() => {
  const { isDev } = useRuntimeConfig().public
  const { configDynatraceUrl, tempDynatraceLoadingConfig } = useFeatureFlags()
  const dtrum = promiseWithResolvers<typeof window.dtrum>()

  if (!configDynatraceUrl) {
    if (!isDev) {
      log.info('Dynatrace agentless Real User Monitoring (RUM) script is not injected.')
    }
    dtrum.reject(null)
    return {
      provide: {
        dtrum: dtrum.promise
      }
    }
  }

  /**
   * On Netlify we need to insert the RUM with JavaScript tag format.
   * Doc: https://docs.dynatrace.com/docs/platform-modules/digital-experience/web-applications/initial-setup/rum-injection#manual-insertion
   */
  const loadingMethod = tempDynatraceLoadingConfig?.method || 'useScript'
  const scriptOptions = tempDynatraceLoadingConfig?.options || {}
  const scriptAttrs = tempDynatraceLoadingConfig?.attrs || {}

  const handleScriptError = (error) => {
    log.error('Failed to load Dynatrace RUM script:', error)
  }

  try {
    switch (loadingMethod) {
      case 'useScriptTag':
        useScriptTag(
          configDynatraceUrl,
          () => {
            dtrum.resolve(window.dtrum)
          },
          {
            ...scriptOptions,
            attrs: scriptAttrs,
            onError: (error) => {
              handleScriptError(error)
            }
          }
        )
        break

      case 'useHead':
        useHead({
          ...(scriptOptions.warmupStrategy
            ? { link: [{ rel: scriptOptions.warmupStrategy, href: configDynatraceUrl }] }
            : {}),
          script: [{
            src: configDynatraceUrl,
            async: true,
            ...scriptAttrs,
            type: 'text/javascript',
            onload: () => {
              dtrum.resolve(window.dtrum)
            },
            onerror: (error) => {
              handleScriptError(error)
            }
          }]
        }, { mode: 'client' })
        break

      case 'useScript':
        useScript(
          {
            src: configDynatraceUrl,
            ...scriptAttrs
          },
          scriptOptions
        ).onLoaded(() => {
          dtrum.resolve(window.dtrum)
        })
        break
    }
  }
  catch (error: any) {
    log.error('Failed to initialize Dynatrace RUM:', error)
  }

  return {
    provide: {
      dtrum: dtrum.promise
    }
  }
})
